import React, { useRef, useState } from 'react';
import { CheckCircle, Flag, PenNib, FilePdf, Eraser, CaretRight } from '@phosphor-icons/react';
import SignatureCanvas from './SignatureCanvas';

/**
 * InvoiceMobile — the contractor's own invoice.
 *
 * They never edit a line: they check the week, sign, and it goes to Michel.
 * Anything wrong is a conversation, not an edit — hence the "report" row
 * instead of inputs. The admin desktop view stays in InvoiceView.
 */

const NAVY = '#16303f';
const TEAL = '#4A7A91';
const RED  = '#cc0000';

const lbl = {
  fontSize: 10.5, fontWeight: 700, letterSpacing: 1.2,
  textTransform: 'uppercase', color: '#9ca3af',
};

const card = { background: '#fff', padding: '14px 16px' };

const weekBtn = {
  width: 44, height: 44, flex: 'none', background: '#fff', border: 'none',
  borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,.06)',
  fontSize: 20, color: NAVY, cursor: 'pointer', lineHeight: 1,
};

function shortRange(from, to) {
  const a = new Date(from + 'T12:00:00');
  const b = new Date(to   + 'T12:00:00');
  const mon = d => d.toLocaleDateString('en-AU', { month: 'short' }).toUpperCase();
  return a.getMonth() === b.getMonth()
    ? `${a.getDate()}–${b.getDate()} ${mon(b)}`
    : `${a.getDate()} ${mon(a)} – ${b.getDate()} ${mon(b)}`;
}

function dayParts(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return {
    dow: d.toLocaleDateString('en-AU', { weekday: 'short' }).toUpperCase(),
    day: d.getDate(),
  };
}

function fmtSignedAt(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

const STATUS_PILL = {
  draft:    { text: 'Not signed', bg: '#fff',     fg: '#6e8899' },
  signed:   { text: 'Signed',     bg: '#dcfce7',  fg: '#15803d' },
  sent:     { text: 'Sent ✓',     bg: '#dcfce7',  fg: '#15803d' },
  approved: { text: 'Approved',   bg: '#dcfce7',  fg: '#15803d' },
  paid:     { text: 'Paid',       bg: '#dcfce7',  fg: '#15803d' },
};

export default function InvoiceMobile({
  invoice, status, onPrevWeek, onNextWeek, onThisWeek,
  isThisWeek, onSign, onPdf, pdfBusy, pdfError, loading, error,
}) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [signing,   setSigning]   = useState(false);
  const [signError, setSignError] = useState('');
  const padRef = useRef(null);

  const signed  = !!status?.signed_at;
  const locked  = ['approved', 'paid'].includes(status?.status);
  const pill    = STATUS_PILL[status?.status || 'draft'] || STATUS_PILL.draft;
  const total   = Number(invoice?.total_pay || 0);
  const items   = invoice?.line_items || [];
  const billable = items.filter(l => !l.pending);

  async function confirmSignature() {
    if (padRef.current?.isEmpty()) { setSignError('Draw your signature first.'); return; }
    setSigning(true); setSignError('');
    try {
      await onSign(padRef.current.toDataURL());
      setSheetOpen(false);
    } catch (e) {
      setSignError(e.message);
    } finally {
      setSigning(false);
    }
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', background: '#e7ecef', minHeight: '60vh' }}>

      {/* ── Intro block — same shape as the Detail Queue ─────────────── */}
      <div style={{ background: '#dce5eb', padding: '14px 16px 16px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.3, color: '#6e8899', textTransform: 'uppercase', marginBottom: 4 }}>
          {invoice
            ? `Week ${invoice.week_number} · ${invoice.iso_year} · FY ${invoice.fy_week}`
            : 'My invoice'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 14 }}>
          <span style={{ fontSize: 28, fontWeight: 800, color: NAVY, letterSpacing: -0.6, lineHeight: 1.05 }}>My Invoice</span>
          <span style={{ fontSize: 13, fontWeight: 700, borderRadius: 22, padding: '6px 14px', background: pill.bg, color: pill.fg, whiteSpace: 'nowrap' }}>
            {pill.text}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={onPrevWeek} style={weekBtn} aria-label="Previous week">‹</button>
          <div style={{ flex: 1, textAlign: 'center', background: '#fff', borderRadius: 12, height: 44, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1.1 }}>
            <span style={{ fontSize: 15, fontWeight: 800, color: NAVY }}>
              {invoice ? shortRange(invoice.week.from, invoice.week.to) : '—'}
            </span>
            {isThisWeek && <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.5, color: TEAL }}>THIS WEEK</span>}
          </div>
          <button onClick={onNextWeek} style={weekBtn} aria-label="Next week">›</button>
          {!isThisWeek && (
            <button onClick={onThisWeek} style={{ ...weekBtn, width: 'auto', padding: '0 12px', fontSize: 11, fontWeight: 800 }}>Today</button>
          )}
        </div>
      </div>

      {/* ── Body ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 0 24px' }}>

        {error   && <div style={{ ...card, color: '#b91c1c', fontSize: 13 }}>{error}</div>}
        {loading && <div style={{ ...card, color: '#6b7280', fontSize: 13 }}>Loading…</div>}

        {invoice && (
          <>
            {/* Total */}
            <div style={card}>
              <div style={lbl}>Total you are invoicing</div>
              <div style={{ fontSize: 32, fontWeight: 800, color: RED, letterSpacing: -1, margin: '4px 0 2px' }}>
                ${total.toFixed(2)}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <span style={{ fontSize: 12.5, color: '#6b7280' }}>
                  {billable.length} item{billable.length !== 1 ? 's' : ''} · GST not applicable
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, background: '#e8f2f7', color: '#1e3a4a', borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap' }}>
                  7-day terms
                </span>
              </div>
              {invoice.total_pending > 0 && (
                <div style={{ marginTop: 10, background: '#fef6da', color: '#8a6410', borderRadius: 8, padding: '8px 10px', fontSize: 12.5, fontWeight: 600 }}>
                  ${Number(invoice.total_pending).toFixed(2)} still waiting for Michel's approval — not on this invoice yet.
                </div>
              )}
            </div>

            {/* Lines */}
            {items.length === 0 ? (
              <div style={{ ...card, color: '#9ca3af', fontSize: 13.5 }}>
                Nothing to invoice for this week yet.
              </div>
            ) : items.map((item, i) => {
              const d = item.date ? dayParts(item.date) : null;
              return (
                <div key={i} style={{ ...card, display: 'flex', alignItems: 'center', gap: 12, opacity: item.pending ? 0.65 : 1 }}>
                  <div style={{ width: 46, flex: 'none', textAlign: 'center' }}>
                    {d ? (
                      <>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.8, color: '#9ca3af' }}>{d.dow}</div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: NAVY, lineHeight: 1.05 }}>{d.day}</div>
                      </>
                    ) : (
                      <div style={{ fontSize: 18, fontWeight: 800, color: NAVY }}>×{item.qty_label || item.qty}</div>
                    )}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: NAVY, lineHeight: 1.2 }}>{item.description}</div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 5, flexWrap: 'wrap' }}>
                      {item.pending ? (
                        <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 999, padding: '2px 8px', background: '#fef6da', color: '#8a6410' }}>Pending</span>
                      ) : (
                        <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 999, padding: '2px 8px', background: '#dcfce7', color: '#15803d' }}>Approved</span>
                      )}
                      {item.rate > 0 && (
                        <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 999, padding: '2px 8px', background: '#e8f2f7', color: '#1e3a4a' }}>
                          ${Number(item.rate).toFixed(2)} each
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
                    <span style={{ fontSize: 16, fontWeight: 800, color: NAVY }}>
                      ${Number(item.pending ? item.pending_pay : item.pay || 0).toFixed(2)}
                    </span>
                    {!item.pending && <CheckCircle size={20} weight="fill" color={TEAL} />}
                  </div>
                </div>
              );
            })}

            {/* Dispute — the only way a contractor changes an invoice */}
            <a
              href={`sms:?&body=${encodeURIComponent(`Hi Michel, about my invoice ${invoice.invoice_number}: `)}`}
              style={{ ...card, display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none', color: '#6b7280' }}
            >
              <Flag size={20} color="#9ca3af" />
              <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>Something missing? Report to Michel</span>
              <CaretRight size={16} color="#c3ccd2" />
            </a>

            {/* Declaration + signature */}
            <div style={card}>
              <div style={lbl}>Declaration</div>
              <p style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.55, margin: '6px 0 12px' }}>
                I confirm the days and amounts above are correct and complete for this week,
                and I authorise ACT's Car Detailing to process this invoice.
              </p>

              {signed ? (
                <div>
                  <div style={{ borderBottom: '1.5px solid #1A1A1A', paddingBottom: 4, minHeight: 54, display: 'flex', alignItems: 'flex-end' }}>
                    <img
                      src={status.signature_png} alt="Signature"
                      style={{ maxHeight: 48, maxWidth: '80%', transform: 'rotate(-2deg)', transformOrigin: 'bottom left' }}
                    />
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>
                    Signed {fmtSignedAt(status.signed_at)} · {status.signed_name || invoice.contractor.name}
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => { setSignError(''); setSheetOpen(true); }}
                  disabled={locked || total <= 0}
                  style={{
                    width: '100%', height: 74, border: '2px dashed #b9c6cf', background: '#f4f7f9',
                    borderRadius: 10, cursor: (locked || total <= 0) ? 'not-allowed' : 'pointer',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3,
                  }}
                >
                  <span style={{ fontSize: 14, fontWeight: 800, color: TEAL }}>Tap to sign</span>
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>
                    {total > 0 ? 'Required before sending' : 'Nothing to sign this week'}
                  </span>
                </button>
              )}
            </div>

            {/* Where the money lands */}
            <div style={card}>
              <div style={{ ...lbl, color: TEAL }}>Paid into</div>
              {invoice.contractor.bsb ? (
                <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'monospace', color: NAVY, marginTop: 5 }}>
                  BSB {invoice.contractor.bsb} · {invoice.contractor.account_number || '—'}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: '#b91c1c', marginTop: 5 }}>
                  No bank details yet — add them in My Account or you can't be paid.
                </div>
              )}
            </div>

            {/* Full document. This used to call window.print(), which does
                nothing in the installed app on a phone — so nobody could get
                a copy out. It now builds the real PDF and hands it to the
                share sheet, or downloads it. */}
            <button
              onClick={onPdf}
              disabled={pdfBusy}
              style={{ ...card, border: 'none', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: pdfBusy ? 'wait' : 'pointer', color: TEAL, fontSize: 14, fontWeight: 700 }}
            >
              <FilePdf size={18} /> {pdfBusy ? 'Preparing PDF…' : 'Get the invoice as PDF'}
            </button>

            {pdfError && (
              <div style={{ ...card, background: '#fee2e2', color: '#b91c1c', fontSize: 13, fontWeight: 600 }}>
                {pdfError}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Action bar ────────────────────────────────────────────────── */}
      {invoice && (
        <div style={{ position: 'sticky', bottom: 0, background: '#fff', borderTop: '1px solid #e2e6e9', padding: '10px 16px', display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            onClick={onPdf}
            disabled={pdfBusy}
            style={{ width: 48, height: 48, flex: 'none', background: '#f3f4f6', border: 'none', borderRadius: 12, cursor: pdfBusy ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            aria-label="Invoice PDF"
          >
            <FilePdf size={22} color={pdfBusy ? '#9ca3af' : NAVY} />
          </button>
          {signed ? (
            <div style={{ flex: 1, height: 48, borderRadius: 12, background: '#dcfce7', color: '#15803d', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 15, fontWeight: 800 }}>
              <CheckCircle size={20} weight="fill" />
              {locked ? 'Approved by Michel' : 'Sent · awaiting payment'}
            </div>
          ) : (
            <button
              onClick={() => { setSignError(''); setSheetOpen(true); }}
              disabled={total <= 0}
              style={{ flex: 1, height: 48, borderRadius: 12, border: 'none', background: total > 0 ? TEAL : '#e5e7eb', color: total > 0 ? '#fff' : '#9ca3af', fontSize: 15, fontWeight: 800, cursor: total > 0 ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            >
              <PenNib size={20} weight="fill" /> Review &amp; sign
            </button>
          )}
        </div>
      )}

      {/* ── Signature sheet ───────────────────────────────────────────── */}
      {sheetOpen && (
        <div
          onClick={() => !signing && setSheetOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 2000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
        >
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', width: '100%', maxWidth: 560, borderRadius: '18px 18px 0 0', padding: '10px 16px 20px' }}>
            <div style={{ width: 40, height: 4, background: '#e2e6e9', borderRadius: 2, margin: '0 auto 14px' }} />

            <div style={{ fontSize: 19, fontWeight: 800, color: NAVY }}>Sign your invoice</div>
            <div style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 14px' }}>
              Draw your signature to confirm ${total.toFixed(2)} for week {invoice?.week_number}.
            </div>

            <SignatureCanvas
              ref={padRef}
              height={150}
              style={{ border: '2px dashed #b9c6cf', borderRadius: 14, background: '#f4f7f9', height: 150 }}
            />
            <div style={{ ...lbl, textAlign: 'center', marginTop: 6 }}>Sign above</div>

            {signError && (
              <div style={{ marginTop: 10, background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '8px 10px', fontSize: 12.5, fontWeight: 600 }}>
                {signError}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button
                onClick={() => padRef.current?.clear()}
                style={{ width: 48, height: 48, flex: 'none', background: '#f3f4f6', border: 'none', borderRadius: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                aria-label="Clear signature"
              >
                <Eraser size={20} color={NAVY} />
              </button>
              <button
                onClick={confirmSignature}
                disabled={signing}
                style={{ flex: 1, height: 48, borderRadius: 12, border: 'none', background: signing ? '#9ca3af' : TEAL, color: '#fff', fontSize: 15, fontWeight: 800, cursor: signing ? 'wait' : 'pointer' }}
              >
                {signing ? 'Signing…' : 'Confirm signature'}
              </button>
            </div>

            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 12, textAlign: 'center' }}>
              Your signature, name, date and IP are stored with the invoice.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
